import { useRef, useState } from 'react'
import { Pencil } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogClose, DialogTrigger } from '@/components/ui/dialog'
import { InlineFieldTextarea } from '@/components/inline-field-group'
import { LoadingButton } from '@/components/loading-button'
import { cn } from '@/lib/utils'
import type { QuoteLine } from '../api/types'
import { EditorModalContent } from './modal'

type Props = {
  line: QuoteLine
  busy: boolean
  /** Saves `{spec_text_override}`; resolves false when the save failed. */
  onSave: (lineId: string, changes: { spec_text_override: string | null }) => Promise<boolean>
  className?: string
}

/**
 * A line's specification wording (demo `QuoteLineSpecEditor`): a two-line preview that opens the
 * full text. Saving replaces the catalog wording for this line only; Reset returns to the catalog.
 * Changing the product or its configuration resets it on the server.
 */
export function QuoteLineSpecEditor(props: Props) {
  const { line } = props
  return <SpecEditor key={`${line.id}:${line.product_id}:${JSON.stringify(line.configuration)}`} {...props} />
}

function SpecEditor({ line, busy, onSave, className }: Props) {
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(false)
  const input = useRef<HTMLTextAreaElement>(null)
  const text = line.spec_text ?? ''
  const edited = line.spec_text_override != null
  const disabled = busy || saving
  const name = line.model || line.description

  const changeOpen = (next: boolean) => {
    if (disabled) return
    if (next) {
      setDraft(text)
      setFailed(false)
    }
    setOpen(next)
  }

  const save = async (value: string | null) => {
    setSaving(true)
    setFailed(false)
    try {
      if (await onSave(line.id, { spec_text_override: value })) setOpen(false)
      else setFailed(true)
    } catch {
      setFailed(true)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog onOpenChange={changeOpen} open={open}>
      <DialogTrigger asChild>
        <button
          aria-label={`Edit specifications for ${name}`}
          className={cn(
            'group/spec mt-1 flex w-full min-w-0 items-start gap-1.5 rounded-sm text-left text-xs leading-4 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 disabled:opacity-50',
            className,
          )}
          disabled={disabled}
          type="button"
        >
          <span className="line-clamp-2 min-w-0 flex-1 [overflow-wrap:anywhere]">{text || 'Add specifications'}</span>
          {edited ? (
            <span className="shrink-0 rounded-full border border-amber-300 bg-amber-50 px-1.5 text-[11px] font-medium leading-4 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300">
              Edited
            </span>
          ) : null}
          <Pencil aria-hidden="true" className="mt-0.5 size-3 shrink-0 opacity-60 group-hover/spec:opacity-100" />
        </button>
      </DialogTrigger>
      <EditorModalContent
        closeDisabled={disabled}
        footer={
          <div className="flex flex-wrap items-center justify-end gap-2">
            {failed ? (
              <p className="w-full text-sm text-destructive" role="alert">
                Specifications were not saved. Try again.
              </p>
            ) : null}
            {edited ? (
              <Button className="mr-auto" disabled={disabled} onClick={() => void save(null)} variant="ghost">
                Reset to catalog
              </Button>
            ) : null}
            <DialogClose asChild>
              <Button disabled={disabled} variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <LoadingButton loading={saving} loadingText="Saving..." disabled={busy} onClick={() => void save(draft)}>
              Save specifications
            </LoadingButton>
          </div>
        }
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          input.current?.focus()
          input.current?.setSelectionRange(0, 0)
        }}
        title={
          <>
            Specifications / {name}
            {edited ? (
              <span className="ml-2 text-sm font-normal text-amber-700 dark:text-amber-400">Edited</span>
            ) : null}
          </>
        }
      >
        <InlineFieldTextarea
          aria-label="Specification text"
          className="min-h-64 resize-y leading-6 sm:h-80"
          disabled={disabled}
          onChange={(event) => setDraft(event.target.value)}
          ref={input}
          value={draft}
        />
      </EditorModalContent>
    </Dialog>
  )
}
